import { adaptProductForTracking } from "@/lib/utils/tracking/itemDataTransformer"
import { type SiteContext } from "@/lib/utils/tracking/siteContext"

type ProductGroupForTracking = { key: string; items: unknown[] }

type TrackProductListViewParams = {
    productGroups: ProductGroupForTracking[]
    listType: "plp" | "plp_show_more"
    listId: string
    listSortOrder: string
    siteContext: SiteContext
    listHierarchy: string
    locale: string
    indexOffset?: number
}

export const trackProductListView = ({
    productGroups,
    listType,
    listId,
    listSortOrder,
    siteContext,
    listHierarchy,
    locale,
    indexOffset = 0
}: TrackProductListViewParams): Promise<boolean> => {
    const groupsWithItems = productGroups.filter(
        group => group.items && group.items.length > 0
    )
    if (groupsWithItems.length === 0) return Promise.resolve(false)

    const itemsToTrack = groupsWithItems.map((group, index) =>
        adaptProductForTracking(group.items[0], {
            item_list_name: listType,
            item_list_id: listId,
            index: indexOffset + index
        })
    )

    return import("@/lib/utils/tracking/gtmEvents").then(({ viewItemListEvent }) =>
        viewItemListEvent(
            itemsToTrack,
            listType,
            listId,
            siteContext,
            listType,
            listHierarchy,
            listSortOrder,
            locale
        )
    )
}

type TrackProductListFromGridParams = {
    extendedProducts: ProductGroupForTracking[]
    productListKey: string
    sortKey: string
    filterKey: string
    listId: string
    listSortOrder: string
    siteContext: SiteContext
    listHierarchy: string
    locale: string
    lastTrackedProductStateRef: { current: string }
    previousProductCountForPaginationRef: { current: number }
}

export const trackProductListFromGrid = async ({
    extendedProducts,
    productListKey,
    sortKey,
    filterKey,
    listId,
    listSortOrder,
    siteContext,
    listHierarchy,
    locale,
    lastTrackedProductStateRef,
    previousProductCountForPaginationRef
}: TrackProductListFromGridParams): Promise<boolean> => {
    const combinedHash = `${productListKey}|${sortKey}|${filterKey}`
    const totalCount = extendedProducts.length
    const previousCount = previousProductCountForPaginationRef.current

    if (lastTrackedProductStateRef.current === combinedHash) {
        return Promise.resolve(true)
    }

    if (totalCount > previousCount && previousCount > 0) {
        const newProducts = extendedProducts.slice(previousCount)
        return trackProductListView({
            productGroups: newProducts,
            listType: "plp_show_more",
            listId,
            listSortOrder,
            siteContext,
            listHierarchy,
            locale,
            indexOffset: previousCount
        }).then(success => {
            if (success) {
                previousProductCountForPaginationRef.current = totalCount
            }
            return success
        })
    }

    return trackProductListView({
        productGroups: extendedProducts,
        listType: "plp",
        listId,
        listSortOrder,
        siteContext,
        listHierarchy,
        locale
    }).then(success => {
        if (success) {
            lastTrackedProductStateRef.current = combinedHash
            previousProductCountForPaginationRef.current = totalCount
        }
        return success
    })
}
